import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DecimalPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  untracked,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconAnchor, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer, MatDrawerContainer, MatDrawerContent, MatDrawerToggleResult } from '@angular/material/sidenav';
import { MatSort } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { LocationService } from '@app/features/admin/database/location/state/location.service';
import { Location } from '@app/features/admin/database/location/state/location.types';
import { PermissionDirective } from '@app/features/admin/settings/permission/directives/permission.directive';
import { SettingsState } from '@app/features/admin/settings/state/settings.state';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { Profile } from '@app/layout/common/profile/state/profile.types';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { SharedActions } from '@shared/shared.actions';
import { LIMIT_PER_PAGE, LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { SharedService } from '@shared/shared.service';
import { SharedState } from '@shared/shared.state';
import { VesselItinerary, VesselItineraryPagination } from '@shared/types/shared.types';
import { sealAnimations, SealFindByKeyPipe, SealMediaWatcherService, SealScrollResetDirective } from '@shipsfocus/seal';
import { EventItemDialogComponent } from '@timesheet/components/event-item-dialog/event-item-dialog.component';
import { TimesheetListComponent } from '@timesheet/list/list.component';
import { TimesheetState } from '@timesheet/state/timesheet.state';
import { Timesheet, TimesheetEvent, TimesheetEventItem, TimesheetEventItemPagination } from '@timesheet/state/timesheet.types';
import { AuthService } from 'app/core/auth/auth.service';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { DateTime } from 'luxon';
import { NgxMaskDirective } from 'ngx-mask';
import { combineLatestWith, debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import { TimesheetActions } from '../state/timesheet.actions';
import { TimesheetService } from '../state/timesheet.service';
import { EventItemEnum, TimesheetStatuses, TimesheetTypes } from '../timesheet.constants';

@Component({
  selector: 'timesheet-details',
  templateUrl: './details.component.html',
  styles: [
    `
      timesheet-details {
        height: 100%;
      }

      .mat-drawer-inner-container {
        overflow: hidden !important;
      }

      .basic-details-grid {
        grid-template-columns: 1fr 1rem 8fr;
      }

      .timesheet-events-grid {
        grid-template-columns: 3.5rem 3.5rem 1fr 2fr 1fr 3.5rem 3.5rem 3.5rem;
      }

      .events-select-grid {
        grid-template-columns: 1fr 8rem;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  animations: sealAnimations,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RouterLink,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatButton,
    MatIconAnchor,
    MatIconButton,
    MatError,
    MatFormField,
    MatLabel,
    MatIcon,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    MatTooltip,
    TranslocoPipe,
    NgxMaskDirective,
    SealFindByKeyPipe,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerActions,
    MatDatepickerApply,
    MatDatepickerCancel,
    MatSuffix,
    DecimalPipe,
    MatSort,
    SealScrollResetDirective,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    MatButtonToggleGroup,
    MatButtonToggle,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatDivider,
    DatetimeLocalPipe,
    PermissionDirective
  ]
})
export class TimesheetDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  @ViewChild('innerDrawer', { static: false }) innerDrawer: MatDrawer;
  @ViewChild('virtualScroll', { static: false }) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('virtualScrollEvents', { static: false }) virtualScrollEvents: CdkVirtualScrollViewport;

  timesheetForm: UntypedFormGroup;
  timesheet: Signal<Timesheet> = computed(() => this.timesheetState.timesheet());
  itinerary: Signal<VesselItinerary[]> = computed(() => this.sharedState.vesselItinerary());
  displayedItinerary: WritableSignal<VesselItinerary[]> = signal([]);
  itineraryPagination: Signal<VesselItineraryPagination> = computed(() => this.sharedState.vesselItineraryPagination());
  eventItems: Signal<TimesheetEventItem[]> = computed(() => this.timesheetState.timesheetEventItems());
  displayedEventItems: WritableSignal<TimesheetEventItem[]> = signal([]);
  eventItemsPagination: Signal<TimesheetEventItemPagination> = computed(() => this.timesheetState.timesheetEventItemsPagination());
  editMode: WritableSignal<boolean> = signal(false);
  formMode: WritableSignal<'all' | 'mandatory' | 'basic' | 'events'> = signal('all');
  drawerMode: WritableSignal<'over' | 'side'> = signal('side');
  innerSliderMode: WritableSignal<'itinerary' | 'location' | 'events' | ''> = signal('');
  profile: Signal<Profile> = computed(() => this._profileState.profile());
  queryParams: WritableSignal<Params> = signal(void 0);
  searchedEventItems: Signal<TimesheetEventItem[]> = computed(() => this.timesheetState.searchedEventItems());
  searchedLocations: Signal<Location[]> = computed(() => this.sharedState.searchedLocations());
  isLoadingMore: WritableSignal<boolean> = signal(false);
  selectedEvent: WritableSignal<TimesheetEvent> = signal(null);
  selectedEventIdx: WritableSignal<number> = signal(null);
  searchEventItemInputControl: FormControl<string> = new FormControl<string>(null);
  searchPortInputControl: FormControl<string> = new FormControl<string>(null);

  timesheetTypes: typeof TimesheetTypes = TimesheetTypes;
  timesheetStatuses: typeof TimesheetStatuses = TimesheetStatuses;

  private _controlSubscriptions: Map<string, Subscription> = new Map<string, Subscription>();
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _injector: Injector,
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private _timesheetListComponent: TimesheetListComponent,
    private _authService: AuthService,
    private _settingsState: SettingsState,
    private _sealMediaWatcherService: SealMediaWatcherService,
    private _popupService: PopupService,
    public timesheetState: TimesheetState,
    private _timesheetActions: TimesheetActions,
    public timesheetService: TimesheetService,
    private _profileState: ProfileState,
    public sharedState: SharedState,
    private _sharedActions: SharedActions,
    private _sharedService: SharedService,
    private _locationService: LocationService,
    private _matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this._timesheetListComponent.detailsDrawer.open().then();

    this.timesheetForm = this._formBuilder.group({
      id: [''],
      master_name: [''],
      voyage_number: ['', [Validators.required]],
      date_timesheet: this._formBuilder.group({
        date: ['', [Validators.required]],
        time: ['']
      }),
      type_id: ['', [Validators.required]],
      port_id: ['', [Validators.required]],
      port_name: [''],
      country_name: [''],
      location_id: [{ value: '', disabled: true }, [Validators.required]],
      location_name: [''],
      cargo_name: ['', [Validators.required]],
      quantity_document: ['', [Validators.required]],
      quantity_ship: ['', [Validators.required]],
      remarks: [''],
      timesheet_events: this._formBuilder.array([]),
      owner_company_id: [this._profileState.ownerCompanyId()],
      vessel_id: ['']
    });

    effect(
      () => {
        const request = this.timesheet();

        untracked(() => {
          this.toggleEditMode(!request?.id);

          if (request?.id) {
            this.formMode.set('events');
          }
        });
      },
      { injector: this._injector }
    );

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set(queryParams);
    });

    this._sealMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }: any) => {
      if (matchingAliases.includes('sm')) {
        this.drawerMode.set('side');
      } else {
        this.drawerMode.set('over');
      }
    });
  }

  ngAfterViewInit(): void {
    const searchChange$ = this.searchEventItemInputControl.valueChanges.pipe(debounceTime(500), takeUntil(this._unsubscribeAll));

    searchChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (search) => {
        this._timesheetActions.actions.getTimesheetEventItems({
          queryParams: {
            page: '1',
            limit: LIMIT_PER_PAGE_DEFAULT,
            'filter[name]': search
          }
        });

        this._timesheetActions.actions.getTimesheetEventItemsSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.displayedEventItems.set([...this.eventItems()]);
        });
      }
    });

    const searchPortChange$ = this.searchPortInputControl.valueChanges.pipe(debounceTime(500), takeUntil(this._unsubscribeAll));

    searchPortChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (search) => {
        const queryParams = {
          page: '1',
          limit: LIMIT_PER_PAGE_DEFAULT
        };

        if (search) {
          Object.assign(queryParams, { 'filter[port_name]': search });
        }

        this._sharedActions.actions.getVesselItineraryById({
          vesselId: this._profileState.profileId(),
          queryParams: queryParams
        });

        this._sharedActions.actions.getVesselItineraryByIdSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.displayedItinerary.set([...this.itinerary()]);
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  closeInnerDrawer() {
    this.innerDrawer.close();
    this.innerSliderMode.set('');
  }

  closeDrawer(): Promise<MatDrawerToggleResult> {
    this._changeDetectorRef.markForCheck();

    return this._timesheetListComponent.detailsDrawer.close();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.timesheetForm.get('timesheet_events')['controls'], event.previousIndex, event.currentIndex);
  }

  displayItems(event: AbstractControl, item_id: number): string {
    return this.searchedEventItems()?.find((item) => item.id === item_id)?.name || event.get('timesheet_event_item_name').value || undefined;
  }

  displayLocations(event: AbstractControl, location_id: number): string {
    return this.searchedLocations()?.find((location) => `${location.id}` === `${location_id}`)?.name || event.get('location_name').value || undefined;
  }

  selectLocationAutofill(event: MatAutocompleteSelectedEvent) {
    const selectedLocationId = event?.option?.value;
    if (!selectedLocationId) {
      return;
    }

    const locationName = this.searchedLocations().find((item) => item.id === selectedLocationId)?.name;

    this.timesheetForm.get('location_name').setValue(locationName);
    this.timesheetForm.get('location_id').setValue(selectedLocationId);

    this._changeDetectorRef.markForCheck();
  }

  selectEventItemAutofill(event: MatAutocompleteSelectedEvent, control: AbstractControl) {
    const selectedEventItemId = event?.option?.value;
    if (!selectedEventItemId) {
      return;
    }

    const itemName = this.searchedEventItems().find((item) => item.id === selectedEventItemId)?.name;

    control.get('timesheet_event_item_name').setValue(itemName);
    control.get('timesheet_event_item_id').setValue(selectedEventItemId);

    this._changeDetectorRef.markForCheck();
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode.update((editMode) => !editMode);
    } else {
      this.editMode.set(editMode);
    }

    if (editMode) {
      this.timesheetForm.reset(this.timesheet());
      (this.timesheetForm.get('timesheet_events') as UntypedFormArray).clear();

      this.timesheetForm.get('port_id').value ? this.timesheetForm.get('location_id').enable() : this.timesheetForm.get('location_id').disable();

      this._setDateTimeValue('date_timesheet', this.timesheet().date_timesheet);

      if (this.timesheet().events?.length > 0) {
        const eventFormGroups = [];

        this.timesheet().events.forEach((event) => {
          const formGroup = this._formBuilder.group({
            seq: [event.sequence],
            timesheet_event_item_id: [event.timesheet_event_item_id],
            timesheet_event_item_name: [event.timesheet_event_item_name],
            datetime_event: this._formBuilder.group({
              date: [''],
              time: ['']
            }),
            datetime_from: this._formBuilder.group({
              date: [''],
              time: ['']
            }),
            datetime_to: this._formBuilder.group({
              date: [''],
              time: ['']
            }),
            flag_lock: [event.flag_lock],
            flag_from_to: [event.flag_from_to],
            cargo: [event.cargo],
            remarks: [event.remarks]
          });

          const setDateTimeValue = (formGroup: UntypedFormGroup, controlName: string, timestamp: number) => {
            if (Object.prototype.hasOwnProperty.call(formGroup.controls, controlName)) {
              formGroup
                .get(controlName)
                ?.get('date')
                .setValue(timestamp ? DateTime.fromSeconds(timestamp).toISO() : null);
              formGroup
                .get(controlName)
                ?.get('time')
                .setValue(
                  timestamp
                    ? `${String(DateTime.fromSeconds(timestamp).get('hour')).padStart(2, '0')}${String(DateTime.fromSeconds(timestamp).get('minute')).padStart(2, '0')}`
                    : null
                );
            }
          };

          setDateTimeValue(formGroup, 'datetime_event', event.datetime_event);
          setDateTimeValue(formGroup, 'datetime_from', event.datetime_from);
          setDateTimeValue(formGroup, 'datetime_to', event.datetime_to);

          eventFormGroups.push(formGroup);
        });

        this.timesheetForm.setControl('timesheet_events', this._formBuilder.array(eventFormGroups));
      }

      this._listenEventItemsValueChange();
      this.timesheetForm
        .get('location_id')
        .valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((query) => {
            return typeof query === 'string'
              ? this._locationService
                  .getAutofillLocations({
                    limit: LIMIT_PER_PAGE,
                    'filter[name]': query,
                    'filter[port_id]': this.timesheetForm.get('port_id').value
                  })
                  .pipe(tap((searchedLocations) => this.sharedState.set({ searchedLocations })))
              : of(null);
          }),
          takeUntil(this._unsubscribeAll)
        )
        .subscribe(() => {
          this.timesheetForm.get('location_id').updateValueAndValidity();
        });

      // this._subscribeToValueChanges('location_id', this._sharedService.searchLocationsByName.bind(this._sharedService));
    } else {
      this._unsubscribeAllControlSubscriptions();
    }

    this._changeDetectorRef.markForCheck();
  }

  private _listenEventItemsValueChange(): void {
    if (this._controlSubscriptions.has('timesheet_events')) {
      this._controlSubscriptions.get('timesheet_events')?.unsubscribe();
    }

    const controls = (this.timesheetForm.get('timesheet_events') as UntypedFormArray).controls;

    const subscriptions = controls.map((control) =>
      control.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((query) => {
            return typeof query?.timesheet_event_item_id === 'string' ? this.timesheetService.searchEventItemsByName(query?.timesheet_event_item_id) : of(null);
          }),
          takeUntil(this._unsubscribeAll)
        )
        .subscribe()
    );

    this._controlSubscriptions.set('timesheet_events', new Subscription(() => subscriptions.forEach((sub) => sub.unsubscribe())));
  }

  private _unsubscribeAllControlSubscriptions(): void {
    this._controlSubscriptions.forEach((subscription) => subscription?.unsubscribe());
    this._controlSubscriptions.clear();

    this.timesheetState.resetTimesheetEventItems();
  }

  selectFormMode(mode: 'all' | 'mandatory' | 'basic' | 'events'): void {
    this.formMode.set(mode);
  }

  saveTimesheet(): void {
    if (this.timesheetForm.invalid) {
      return;
    }

    if (this.timesheet().id) {
      this._handleTimesheetUpdate();
    } else {
      this._handleTimesheetCreation();
    }
  }

  deleteTimesheet(): void {
    this._popupService.openConfirmationDialog({
      title: this.translocoService.translate('ship_interface.delete_timesheet'),
      message: this.translocoService.translate('ship_interface.are_you_sure_you_want_to_delete_this_timesheet_this_action_cannot_be_undone'),
      actionType: 'delete',
      confirmButtonColor: 'warn',
      dismissible: true,
      onAccept: () => {
        this._timesheetActions.actions.deleteTimesheet({
          id: this.timesheet().id,
          activatedRoute: this._activatedRoute
        });
        this._timesheetActions.actions.deleteTimesheetSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.toggleEditMode(false);
        });
      },
      onCancel: () => {}
    });
  }

  addEventField(): void {
    (this.timesheetForm.get('timesheet_events') as UntypedFormArray).push(
      this._formBuilder.group({
        seq: [''],
        timesheet_event_item_id: [''],
        timesheet_event_item_name: [''],
        datetime_event: this._formBuilder.group({
          date: [''],
          time: ['']
        }),
        datetime_from: this._formBuilder.group({
          date: [''],
          time: ['']
        }),
        datetime_to: this._formBuilder.group({
          date: [''],
          time: ['']
        }),
        flag_lock: [0],
        flag_from_to: [0],
        cargo: [''],
        remarks: ['']
      })
    );

    this.timesheetState.resetSearchedEventItems();

    this._listenEventItemsValueChange();

    this._changeDetectorRef.markForCheck();
  }

  removeEventField(index: number): void {
    (this.timesheetForm.get('timesheet_events') as UntypedFormArray).removeAt(index);

    this.timesheetState.resetSearchedEventItems();

    this._listenEventItemsValueChange();
  }

  toggleControl(control: AbstractControl): void {
    const prevValue = control.value;
    control.setValue(prevValue === 1 ? 0 : 1);
  }

  resetDates(event: AbstractControl): void {
    event.get('datetime_event').get('date').reset();
    event.get('datetime_event').get('time').reset();

    event.get('datetime_from').get('date').reset();
    event.get('datetime_from').get('time').reset();

    event.get('datetime_to').get('date').reset();
    event.get('datetime_to').get('time').reset();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private _handleTimesheetCreation(): void {
    const timesheet = this.timesheetForm.getRawValue();
    const body = new URLSearchParams();

    const parsedDateTime = this._parseDateTime(timesheet['date_timesheet']);
    timesheet['date_timesheet'] = parsedDateTime ? parsedDateTime.valueOf() / 1000 : '';

    Object.entries(timesheet).forEach(([key, value]: any) => {
      if (key !== 'timesheet_events') {
        body.set(key, value || value === 0 ? String(value) : '');
      }
    });

    delete timesheet['timesheet_events'];

    this._timesheetActions.actions.createTimesheet({
      timesheet: body,
      activatedRoute: this._activatedRoute
    });
  }

  private _handleTimesheetUpdate(): void {
    const timesheet = this.timesheetForm.getRawValue();
    const body = new URLSearchParams();
    const eventsBody = new URLSearchParams();

    const parsedDateTime = this._parseDateTime(timesheet['date_timesheet']);
    timesheet['date_timesheet'] = parsedDateTime ? parsedDateTime.valueOf() / 1000 : '';

    Object.entries(timesheet).forEach(([key, value]: any) => {
      if (key !== 'timesheet_events') {
        body.set(key, value || value === 0 ? String(value) : '');
      }
    });

    if (timesheet.timesheet_events && timesheet.timesheet_events.length > 0) {
      timesheet.timesheet_events.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]: any) => {
          if (key !== 'seq') {
            if (key !== 'datetime_event' && key !== 'datetime_from' && key !== 'datetime_to') {
              eventsBody.set(`timesheet_event[${index}][${key}]`, value || value === 0 ? String(value) : '');
            } else {
              const parsedDateTime = this._parseDateTime(value);
              value = parsedDateTime ? parsedDateTime.valueOf() / 1000 : '';

              eventsBody.set(`timesheet_event[${index}][${key}]`, value || value === 0 ? String(value) : '');
            }
          } else {
            eventsBody.set(`timesheet_event[${index}][${key}]`, String(index + 1));
          }
        });
      });
    }

    this._timesheetActions.actions.updateTimesheet({
      id: timesheet.id,
      timesheet: body
    });

    this._timesheetActions.actions.updateTimesheetEvents({
      id: timesheet.id,
      timesheet: eventsBody
    });

    this._timesheetActions.actions.updateTimesheetSuccess$
      .pipe(combineLatestWith(this._timesheetActions.actions.updateTimesheetEventsSuccess$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this._timesheetActions.actions.getTimesheets({});
          this._timesheetActions.actions.getTimesheetById({
            id: timesheet.id
          });

          this._timesheetActions.actions.getTimesheetByIdSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.toggleEditMode(false);
          });
        }
      });

    // this._timesheetActions.actions.updateTimesheetEventsSuccess$.pipe(take(1)).subscribe(() => {
    //
    //   this._timesheetActions.actions.getTimesheets({});
    //   this._timesheetActions.actions.getTimesheetById({
    //     id: timesheet.id,
    //   })
    //
    //   this._timesheetActions.actions.getTimesheetByIdSuccess$.pipe(take(1)).subscribe(() => {
    //     this.toggleEditMode(false);
    //   })
    // });
  }

  private _parseDateTime(dateTimeObj: any) {
    return dateTimeObj.date ? DateTime.fromISO(dateTimeObj.date).set(this._parseTime(dateTimeObj.time)) : null;
  }

  private _parseTime(time: string): { hour: number; minute: number } {
    let hours = parseInt(time?.slice(0, 2), 10);
    let minutes = parseInt(time?.slice(-2), 10);

    if (hours < 0 || hours > 23 || isNaN(hours)) {
      hours = 0;
    }

    if (minutes < 0 || minutes > 59 || isNaN(minutes)) {
      minutes = 0;
    }

    return { hour: hours, minute: minutes };
  }

  private _setDateTimeValue(controlName: string, timestamp: number, isToday: boolean = false): void {
    const now = isToday ? DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISO() : null;

    if (Object.prototype.hasOwnProperty.call(this.timesheetForm.controls, controlName)) {
      this.timesheetForm
        .get(controlName)
        ?.get('date')
        .setValue(timestamp ? DateTime.fromSeconds(timestamp).toISO() : now);
      this.timesheetForm
        .get(controlName)
        ?.get('time')
        .setValue(
          timestamp
            ? `${String(DateTime.fromSeconds(timestamp).get('hour')).padStart(2, '0')}${String(DateTime.fromSeconds(timestamp).get('minute')).padStart(2, '0')}`
            : isToday
              ? '0000'
              : null
        );
    }
  }

  // ------ Timesheet events --------
  openEventsDrawer(event: AbstractControl, index: number) {
    this.timesheetState.resetTimesheetEventItems();

    this.selectedEvent.set(event.getRawValue());
    this.selectedEventIdx.set(index);

    this._timesheetActions.actions.getTimesheetEventItems({});

    this._timesheetActions.actions.getTimesheetEventItemsSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.displayedEventItems.set(this.eventItems());
      this.innerSliderMode.set('events');
      this.innerDrawer.open().then(() => {
        this.virtualScroll?.scrollToIndex(0, 'smooth');
        this.virtualScroll?.checkViewportSize();
      });
    });
  }

  onScrollEvents() {
    if (this.eventItemsPagination().current_page < this.eventItemsPagination().last_page && !this.isLoadingMore()) {
      const end = this.virtualScrollEvents.measureScrollOffset('bottom') === 0;

      if (this.eventItems()?.length > 0 && end) {
        this.isLoadingMore.set(true);

        const queryParams = {
          'filter[name]': this.searchEventItemInputControl.value || ''
        };

        this._timesheetActions.actions.getTimesheetEventItems({
          queryParams: {
            page: this.eventItemsPagination().current_page + 1,
            limit: this.eventItemsPagination().per_page,
            ...queryParams
          }
        });

        this._timesheetActions.actions.getTimesheetEventItemsSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.isLoadingMore.set(false);

          this.displayedEventItems.update((prevValue) => [...prevValue, ...this.eventItems()]);
        });
      }
    }
  }

  selectEvent(event: TimesheetEventItem) {
    const eventControl = (this.timesheetForm.get('timesheet_events') as UntypedFormArray).at(this.selectedEventIdx());

    if (event.id === EventItemEnum.OTHERS) {
      const dialogRef = this._matDialog.open(EventItemDialogComponent, {
        panelClass: 'event-item-dialog',
        data: {}
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((manualInput) => {
          if (manualInput) {
            eventControl.get('timesheet_event_item_name').setValue(manualInput);
            eventControl.get('timesheet_event_item_id').setValue(EventItemEnum.OTHERS);

            this.closeInnerDrawer();
          }

          this.searchEventItemInputControl.reset();
        });
    } else {
      eventControl.get('timesheet_event_item_id').setValue(event.id);
      eventControl.get('timesheet_event_item_name').setValue(event.name);

      this.closeInnerDrawer();
    }
  }

  // ------ Itinerary details --------
  openItineraryDrawer() {
    this.sharedState.resetVesselItinerary();

    this._sharedActions.actions.getVesselItineraryById({
      vesselId: this._profileState.get('profile').id
    });

    this._sharedActions.actions.getVesselItineraryByIdSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.displayedItinerary.set(this.itinerary());
      this.innerSliderMode.set('itinerary');
      this.innerDrawer.open().then(() => {
        this.virtualScroll?.scrollToIndex(0, 'smooth');
        this.virtualScroll?.checkViewportSize();
      });
    });
  }

  onScroll() {
    if (this.itineraryPagination().current_page < this.itineraryPagination().last_page && !this.isLoadingMore()) {
      const end = this.virtualScroll.measureScrollOffset('bottom') === 0;

      if (this.itinerary()?.length > 0 && end) {
        this.isLoadingMore.set(true);

        const queryParams = {
          'filter[port_name]': this.searchPortInputControl.value || ''
        };

        this._sharedActions.actions.getVesselItineraryById({
          vesselId: this._profileState.get('profile').id,
          queryParams: {
            page: this.sharedState.vesselItineraryPagination().current_page + 1,
            limit: this.sharedState.vesselItineraryPagination().per_page,
            ...queryParams
          }
        });

        this._sharedActions.actions.getVesselItineraryByIdSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.isLoadingMore.set(false);

          this.displayedItinerary.update((prevValue) => [...prevValue, ...this.itinerary()]);
        });
      }
    }
  }

  selectPort(port: any) {
    this.timesheetForm.get('port_id').setValue(port.port_id);
    this.timesheetForm.get('port_name').setValue(port.port_name);
    this.timesheetForm.get('country_name').setValue(port.port_country_name);

    this.timesheetForm.get('location_id').reset();
    this.timesheetForm.get('location_name').reset();

    this.timesheetForm.get('port_id').value ? this.timesheetForm.get('location_id').enable() : this.timesheetForm.get('location_id').disable();

    this.sharedState.resetSearchedLocations();

    this.searchPortInputControl.reset();

    this.closeInnerDrawer();
  }
}
